import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { postData, ImageBaseUrl } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function VehicleList() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [apiResponse, setApiResponse] = useState(null);
  const rowsPerPage = 10;

  const fetchVehicles = useCallback(
    async (page = 0, status = "all") => {
      setLoading(true);
      try {
        const apiResponse = await postData("/api/vehicle/list", {
          page: page + 1,
          limit: rowsPerPage,
          status: status === "all" ? undefined : status,
          search: searchTerm || undefined,
        });
        setApiResponse(apiResponse);
        if (apiResponse?.success) {
          const vehiclesData = apiResponse?.data?.vehicles || [];
          if (Array.isArray(vehiclesData)) {
            setVehicles(vehiclesData.flat());
          } else {
            setVehicles(vehiclesData);
          }
          setTotalItems(apiResponse?.data?.totalItems || 0);
          setPageCount(apiResponse?.data?.totalPages || 1);
        } else {
          throw new Error(apiResponse?.error || "Failed to fetch vehicles");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Error fetching vehicles",
        });
        console.error("Fetch vehicles error:", error);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, rowsPerPage]
  );

  useEffect(() => {
    fetchVehicles(currentPage, statusFilter);
  }, [currentPage, statusFilter, fetchVehicles]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0); // Reset to first page when applying filters
    fetchVehicles(0, searchTerm, statusFilter);
  };

  const handleReset = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(0);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData("/api/vehicle/delete", {
          vehicleId: id,
        });

        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Vehicle deleted successfully",
          });
          fetchVehicles(currentPage, statusFilter);
        } else {
          throw new Error(response?.error || "Failed to delete vehicle");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Error deleting vehicle",
        });
        console.error("Delete vehicle error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          {/* Vehicle List Header */}
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">All Vehicles</h4>
              <p className="text-muted mb-0">
                Manage and view all registered vehicles
              </p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate("/add-manufacturers-company-subcategory")
                }
                disabled={loading}
              >
                {loading ? (
                  <span>Loading...</span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                {loading ? "Adding..." : "Add Vehicle"}
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search Vehicles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? <span>Loading...</span> : null}
                          {loading ? "Searching..." : "Apply Filters"}
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <CustomLoader loading={true} />
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Vehicle Name</th>
                            <th>Category</th>
                            <th>Subcategory</th>
                            <th>Status</th>
                            <th width="120px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicles.length > 0 ? (
                            vehicles.map((vehicle, index) => (
                              <tr key={vehicle._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  {vehicle.image ? (
                                    <img
                                      src={vehicle.image}
                                      alt={vehicle.vehicleName}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <div className="text-muted">No Image</div>
                                  )}
                                </td>
                                <td>{vehicle.vehicleName}</td>
                                <td>{vehicle.category?.title || "N/A"}</td>
                                <td>{vehicle.subCategory?.title || "N/A"}</td>
                                <td>
                                  <span
                                    className={`badge ${
                                      vehicle.isActive
                                        ? "bg-success"
                                        : "bg-secondary"
                                    }`}
                                  >
                                    {vehicle.isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(
                                          "/update-manufacturers-company-subcategory",
                                          { state: { vehicleData: vehicle } }
                                        )
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(vehicle._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No vehicles found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {pageCount > 1 && (
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            Showing {currentPage * rowsPerPage + 1} to{" "}
                            {Math.min(
                              (currentPage + 1) * rowsPerPage,
                              totalItems
                            )}{" "}
                            of {totalItems} entries
                          </div>
                          <ReactPaginate
                            previousLabel={
                              <i className="mdi mdi-chevron-left"></i>
                            }
                            nextLabel={
                              <i className="mdi mdi-chevron-right"></i>
                            }
                            breakLabel="..."
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageChange}
                            containerClassName="pagination pagination-rounded justify-content-end mt-3"
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="page-item"
                            previousLinkClassName="page-link"
                            nextClassName="page-item"
                            nextLinkClassName="page-link"
                            activeClassName="active"
                            forcePage={currentPage}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VehicleList;
